import React, { useState, useEffect, useRef } from 'react';
import { Container, Content, Loader, Message } from 'rsuite';
import { collection, query, where, orderBy as firestoreOrderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { PeriodType, DatePeriod, OverallMetrics, MarginMetrics, PPCMetrics } from '@/types/primaryLedgerSnapshots';
import { usePrimaryLedger } from '@/components/primary-ledger/PrimaryLedgerContext';
import { primaryLedgerService } from '@/services/primaryLedgerService';
import {
  getLastCompleteWeek,
  getPreviousPeriod,
  navigatePeriod,
  calculatePeriodFromDate
} from '@/utils/primaryLedger/periodCalculations';
import {
  calculateOverallMetrics,
  calculateMarginMetrics,
  calculatePPCMetrics
} from '@/utils/primaryLedger/snapshotCalculations';
import PeriodSelector from '@/components/primary-ledger/snapshots/PeriodSelector';
import OverallResultsSection from '@/components/primary-ledger/snapshots/OverallResultsSection';
import MarginMetricsSection from '@/components/primary-ledger/snapshots/MarginMetricsSection';
import PPCMetricsSection from '@/components/primary-ledger/snapshots/PPCMetricsSection';

const sanitizeCompanyName = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '_')
    .replace(/_+/g, '_');
};

const Snapshots: React.FC = () => {
  const { selectedCompany, selectedAsin, companies } = usePrimaryLedger();
  
  const [periodType, setPeriodType] = useState<PeriodType>('daily');
  const [currentPeriod, setCurrentPeriod] = useState<DatePeriod | null>(null);
  const hasInitialized = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  
  const [overallMetrics, setOverallMetrics] = useState<OverallMetrics | null>(null);
  const [marginMetrics, setMarginMetrics] = useState<MarginMetrics | null>(null);
  const [ppcMetrics, setPpcMetrics] = useState<PPCMetrics | null>(null);

  const findMostRecentDataDate = async (): Promise<DatePeriod | null> => {
    if (!selectedCompany || !selectedAsin) return null;

    const company = companies.find(c => c.id === selectedCompany);
    if (!company) return null;

    const today = new Date();
    const maxDaysBack = 10;

    for (let daysBack = 0; daysBack < maxDaysBack; daysBack++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() - daysBack);
      const period = calculatePeriodFromDate(checkDate, 'daily');

      try {
        const [salesData, ppcData, marginData] = await Promise.all([
          primaryLedgerService.getSalesProfitHistory(
            selectedCompany,
            company.companyName,
            selectedAsin,
            period.startDate,
            period.endDate
          ),
          primaryLedgerService.getPPCResults(
            selectedCompany,
            company.companyName,
            selectedAsin,
            period.startDate,
            period.endDate
          ),
          primaryLedgerService.getMarginHistory(
            selectedCompany,
            company.companyName,
            selectedAsin,
            period.startDate,
            period.endDate
          )
        ]);

        if (salesData.length > 0 || ppcData.length > 0 || marginData.length > 0) {
          return period;
        }
      } catch (err) {
        console.error(`Error checking data for ${period.label}:`, err);
      }
    }

    return calculatePeriodFromDate(today, 'daily');
  };

  const checkDataAvailability = async (
    companyId: string,
    companyName: string,
    asin: string,
    startDate: Date,
    endDate: Date
  ): Promise<boolean> => {
    try {
      const salesData = await primaryLedgerService.getSalesProfitHistory(
        companyId,
        companyName,
        asin,
        startDate,
        endDate
      );
      return salesData.length > 0;
    } catch (err) {
      console.error('Error checking data availability:', err);
      return false;
    }
  };

  useEffect(() => {
    const initializePeriod = async () => {
      if (selectedCompany && selectedAsin && !hasInitialized.current) {
        hasInitialized.current = true;
        setLoading(true);
        setError(null);
        
        try {
          const period = await findMostRecentDataDate();
          if (period) {
            setCurrentPeriod(period);
          }
        } catch (err) {
          console.error('Error initializing period:', err);
          const today = new Date();
          setCurrentPeriod(calculatePeriodFromDate(today, 'daily'));
        } finally {
          setLoading(false);
        }
      }
    };

    initializePeriod();
  }, [selectedCompany, selectedAsin, companies]);

  const fetchSnapshotData = async () => {
    if (!selectedCompany || !selectedAsin || !currentPeriod) return;

    const company = companies.find(c => c.id === selectedCompany);
    if (!company) return;

    setLoading(true);
    setError(null);
    setNoDataAvailable(false);

    try {
      const previousPeriod = getPreviousPeriod(currentPeriod);

      const [currentSales, previousSales, currentPPC, previousPPC, currentMargin, previousMargin] = await Promise.all([
        primaryLedgerService.getSalesProfitHistory(
          selectedCompany,
          company.companyName,
          selectedAsin,
          currentPeriod.startDate,
          currentPeriod.endDate
        ),
        primaryLedgerService.getSalesProfitHistory(
          selectedCompany,
          company.companyName,
          selectedAsin,
          previousPeriod.startDate,
          previousPeriod.endDate
        ),
        primaryLedgerService.getPPCResults(
          selectedCompany,
          company.companyName,
          selectedAsin,
          currentPeriod.startDate,
          currentPeriod.endDate
        ),
        primaryLedgerService.getPPCResults(
          selectedCompany,
          company.companyName,
          selectedAsin,
          previousPeriod.startDate,
          previousPeriod.endDate
        ),
        primaryLedgerService.getMarginHistory(
          selectedCompany,
          company.companyName,
          selectedAsin,
          currentPeriod.startDate,
          currentPeriod.endDate
        ),
        primaryLedgerService.getMarginHistory(
          selectedCompany,
          company.companyName,
          selectedAsin,
          previousPeriod.startDate,
          previousPeriod.endDate
        )
      ]);

      const overall = calculateOverallMetrics(currentSales, previousSales);
      const margin = calculateMarginMetrics(currentMargin, previousMargin);
      const ppc = calculatePPCMetrics(currentPPC, previousPPC);

      if (!overall && !margin && !ppc) {
        setNoDataAvailable(true);
        setOverallMetrics(null);
        setMarginMetrics(null);
        setPpcMetrics(null);
        return;
      }

      setOverallMetrics(overall);
      setMarginMetrics(margin);
      setPpcMetrics(ppc);
    } catch (err) {
      setError('Failed to load snapshot data. Please try again.');
      console.error('Snapshot fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentPeriod) {
      fetchSnapshotData();
    }
  }, [selectedCompany, selectedAsin, currentPeriod]);

  const handlePeriodTypeChange = (type: PeriodType) => {
    setPeriodType(type);
    const today = new Date();
    const newPeriod = calculatePeriodFromDate(today, type);
    setCurrentPeriod(newPeriod);
  };

  const handleNavigate = (direction: 'previous' | 'next') => {
    if (!currentPeriod) return;
    const dir = direction === 'previous' ? 'prev' : 'next';
    const newPeriod = navigatePeriod(currentPeriod, dir);
    setCurrentPeriod(newPeriod);
  };

  const handleCurrent = () => {
    const today = new Date();
    const newPeriod = calculatePeriodFromDate(today, periodType);
    setCurrentPeriod(newPeriod);
  };

  const handleJump = (date: Date) => {
    const newPeriod = calculatePeriodFromDate(date, periodType);
    setCurrentPeriod(newPeriod);
  };

  return (
    <Container className="h-full">
      <Content className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Snapshots</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Period-based performance metrics with trend analysis
          </p>
        </div>

        {!selectedCompany || !selectedAsin ? (
          <Message type="info" showIcon>
            Please select a company and ASIN from the sidebar to view snapshot data.
          </Message>
        ) : (
          <>
            {currentPeriod && (
              <PeriodSelector
                periodType={periodType}
                period={currentPeriod}
                onPeriodTypeChange={handlePeriodTypeChange}
                onNavigate={handleNavigate}
                onCurrent={handleCurrent}
                onJump={handleJump}
              />
            )}

            {noDataAvailable && (
              <Message type="warning" showIcon className="mb-4">
                <div>
                  <strong>Data Not Available</strong>
                  <p className="mt-1">Data for this period is not available yet. Amazon data is typically delayed by 24-48 hours. Try selecting an earlier period.</p>
                </div>
              </Message>
            )}

            {error && !noDataAvailable && (
              <Message type="error" showIcon className="mb-4">
                {error}
              </Message>
            )}

            {loading && (
              <div className="flex justify-center items-center h-64">
                <Loader content={!currentPeriod ? "Finding most recent data..." : "Loading snapshot data..."} size="md" />
              </div>
            )}

            {!loading && !error && !noDataAvailable && (
              <>
                {overallMetrics && <OverallResultsSection metrics={overallMetrics} />}
                {marginMetrics && <MarginMetricsSection metrics={marginMetrics} />}
                {ppcMetrics && <PPCMetricsSection metrics={ppcMetrics} />}
              </>
            )}
          </>
        )}
      </Content>
    </Container>
  );
};

export default Snapshots;